import { isServer, MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { captureError } from "@/shared/observability/capture";

/**
 * RSC-aware QueryClient factory.
 *   - Server: a fresh client per request — never share across requests or one
 *     user's data leaks into another's render.
 *   - Browser: one singleton for the tab's lifetime.
 *
 * Short gcTime (90s) is belt-and-suspenders against cross-tenant cache leaks:
 * the org-switch flow already removes scoped keys, but any stray entry that
 * survives is GC'd within 90s.
 */
function makeQueryClient(): QueryClient {
  return new QueryClient({
    // Central capture for every query/mutation failure — the closed loop's
    // single funnel for data errors (no per-call try/catch sprawl). No-ops on
    // the server; browser-only via captureError's isBrowser guard.
    queryCache: new QueryCache({
      onError: (error) => captureError(error, { source: "queryCache" }),
    }),
    mutationCache: new MutationCache({
      onError: (error) => captureError(error, { source: "mutationCache" }),
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 90_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: 1,
        refetchOnMount: true,
      },
      mutations: { retry: 0 },
    },
  });
}

let browserClient: QueryClient | undefined;

export function getQueryClient(): QueryClient {
  if (isServer) return makeQueryClient();
  if (!browserClient) browserClient = makeQueryClient();
  return browserClient;
}
